/**
 * PricingTierEditor Component
 * Editor for quantity-based pricing tiers
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PricingTier } from '@/types/quotations';
import { EMPTY_PRICING_TIER } from '@/types/quotations';

interface PricingTierEditorProps {
  tiers: PricingTier[];
  onChange: (tiers: PricingTier[]) => void;
  currency?: string;
  disabled?: boolean;
}

export const PricingTierEditor: React.FC<PricingTierEditorProps> = ({
  tiers,
  onChange,
  currency = 'TWD',
  disabled = false,
}) => {
  const { t } = useTranslation('psi');

  const handleTierChange = (index: number, field: keyof PricingTier, value: string | number | null) => {
    const newTiers = [...tiers];
    if (field === 'min_qty' || field === 'max_qty') {
      const numValue = value === '' || value === null ? null : Number(value);
      if (field === 'max_qty') {
        newTiers[index] = { ...newTiers[index], max_qty: numValue };
      } else {
        newTiers[index] = { ...newTiers[index], min_qty: numValue ?? 0 };
      }
    } else if (field === 'unit_price') {
      newTiers[index] = { ...newTiers[index], unit_price: Number(value) || 0 };
    }
    onChange(newTiers);
  };

  const addTier = () => {
    // Calculate new min_qty based on previous tier's max_qty
    const lastTier = tiers[tiers.length - 1];
    const newMinQty = lastTier?.max_qty ? lastTier.max_qty + 1 : (lastTier?.min_qty || 0) + 100;

    const newTier: PricingTier = {
      ...EMPTY_PRICING_TIER,
      min_qty: newMinQty,
      max_qty: null,
    };
    onChange([...tiers, newTier]);
  };

  const removeTier = (index: number) => {
    if (tiers.length <= 1) return; // Keep at least one tier
    const newTiers = tiers.filter((_, i) => i !== index);
    onChange(newTiers);
  };

  // Validate tier ordering
  const validateTiers = (): string | null => {
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      if (tier.min_qty < 0) {
        return t('quotations.validation.minQtyNegative', { index: i + 1 });
      }
      if (tier.max_qty !== null && tier.max_qty !== undefined && tier.max_qty < tier.min_qty) {
        return t('quotations.validation.maxLessThanMin', { index: i + 1 });
      }
      if (i > 0) {
        const prevTier = tiers[i - 1];
        if (prevTier.max_qty !== null && prevTier.max_qty !== undefined && tier.min_qty <= prevTier.max_qty) {
          return t('quotations.validation.overlappingTiers', { index: i + 1 });
        }
      }
    }
    return null;
  };

  const validationError = validateTiers();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('quotations.dialog.pricingTiers')}
        </label>
        {!disabled && (
          <button
            type="button"
            onClick={addTier}
            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            + {t('quotations.dialog.addTier')}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {tiers.map((tier, index) => (
          <div
            key={index}
            className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
          >
            {/* Min Qty */}
            <div className="flex-1">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('quotations.dialog.minQty')}
              </label>
              <input
                type="number"
                min="0"
                value={tier.min_qty}
                onChange={(e) => handleTierChange(index, 'min_qty', e.target.value)}
                disabled={disabled}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Separator */}
            <span className="text-gray-400 self-end pb-1">-</span>

            {/* Max Qty */}
            <div className="flex-1">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('quotations.dialog.maxQty')}
              </label>
              <input
                type="number"
                min={tier.min_qty || 0}
                value={tier.max_qty ?? ''}
                placeholder="∞"
                onChange={(e) => handleTierChange(index, 'max_qty', e.target.value)}
                disabled={disabled}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         disabled:bg-gray-100 dark:disabled:bg-gray-800
                         placeholder:text-gray-400"
              />
            </div>

            {/* Colon separator */}
            <span className="text-gray-400 self-end pb-1">:</span>

            {/* Unit Price */}
            <div className="flex-1">
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('quotations.dialog.unitPrice')} ({currency})
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={tier.unit_price}
                onChange={(e) => handleTierChange(index, 'unit_price', e.target.value)}
                disabled={disabled}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>

            {/* Remove button */}
            {!disabled && tiers.length > 1 && (
              <button
                type="button"
                onClick={() => removeTier(index)}
                className="self-end p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                title={t('common.delete')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Validation error */}
      {validationError && (
        <p className="text-sm text-red-500 dark:text-red-400">{validationError}</p>
      )}

      {/* Helper text */}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('quotations.dialog.tierHint')}
      </p>
    </div>
  );
};

export default PricingTierEditor;
